<template>
  <v-card flat class="pa-3 mt-1">
    <v-card-title class="py-2">
      <v-row align="center" no-gutters>
        <v-col cols="12" sm="4" class="d-flex justify-start mb-2 mb-sm-0">
          <v-btn x-small elevation="10" class="text-xs font-weight-semibold" color="info darken-2" :to="{ name: 'manager.investments.list' }">
            <font-awesome-icon icon="fa-solid fa-list" class="pe-1" />
            Investments
          </v-btn>
        </v-col>
        <v-col cols="12" sm="4" class="d-flex justify-center mb-2 mb-sm-0">
          <v-img :src="require('@/assets/images/logos/logo.png')" max-width="180px" alt="logo" contain />
        </v-col>
        <v-col cols="12" sm="4" class="d-flex justify-end">
          <v-btn x-small elevation="10" class="text-xs font-weight-semibold" color="success darken-2" :disabled="submitting" :loading="loadingRef" @click="getPaymentRef()">
            <font-awesome-icon icon="fa-solid fa-rotate" class="pe-1" />
            New Ref
          </v-btn>
        </v-col>
      </v-row>
    </v-card-title>

    <v-form>
      <v-card-text class="pt-1">
        <v-row>
          <v-col cols="12" sm="6" md="4" class="py-0 my-1">
            <v-text-field v-model="userQuery" outlined dense label="Search user" @keyup.enter="getUsers()" />
          </v-col>
          <v-col cols="12" sm="2" md="1" class="py-0 my-1">
            <v-btn color="success" fab small @click="getUsers()">
              <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
            </v-btn>
          </v-col>

          <v-col cols="12" sm="6" md="4" class="py-0 my-1">
            <v-select v-model="selectedUserId" :items="users" item-text="username" item-value="id" outlined dense label="Select user" @change="loadInvestorDetails()" />
          </v-col>

          <v-col cols="12" sm="6" md="4" class="py-0 my-1">
            <v-text-field v-model="phoneNumber" outlined dense label="Phone" disabled />
          </v-col>

          <v-col cols="12" class="py-0 my-1">
            <v-select v-model="selectedInvestmentId" :items="investments" item-text="label" item-value="id" outlined dense label="Select Investment" @change="loadInvestmentDetails()" />
          </v-col>

          <!-- Penalty panel with multi-selection + bouton individuel -->
          <v-col cols="12" class="py-0 my-1" v-if="investmentDetails && investmentDetails.payments_with_penalty && investmentDetails.payments_with_penalty.length">
            <v-card flat color="red lighten-5" class="pa-3">
              <div class="d-flex flex-wrap justify-space-between align-center mb-2">
                <div class="caption font-weight-bold red--text">
                  <v-icon small color="red">mdi-alert-circle</v-icon>
                  Penalties on this investment
                </div>
                <v-btn small color="red" dark :disabled="selectedPenalties.length === 0" :loading="massCancelling" @click="openMassCancelDialog">
                  Remove selected ({{ selectedPenalties.length }})
                </v-btn>
              </div>

              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th><v-checkbox v-model="selectAll" :indeterminate="selectAll === null" hide-details dense></v-checkbox></th>
                      <th>Month</th>
                      <th>Penalty (FCFA)</th>
                      <th>Status</th>
                      <th class="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in investmentDetails.payments_with_penalty" :key="p.id">
                      <td><v-checkbox v-model="selectedPenalties" :value="p.id" hide-details dense></v-checkbox></td>
                      <td>Month {{ p.month_number }}</td>
                      <td class="red--text font-weight-bold">{{ formatAmount(p.penalty_applied) }}</td>
                      <td>
                        <v-chip x-small :color="p.status === 'completed' ? 'teal' : 'orange'" dark>
                          {{ p.status }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-btn
                          x-small
                          color="red darken-1"
                          dark
                          :loading="singleCancellingId === p.id"
                          :disabled="massCancelling || singleCancellingId !== null"
                          @click="openSingleCancelDialog(p)"
                        >
                          <v-icon x-small class="mr-1">mdi-cancel</v-icon>
                          Remove
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>
          </v-col>

          <!-- Dialog cancel pénalité individuelle -->
          <v-dialog v-model="singleCancelDialog.open" max-width="460">
            <v-card>
              <v-card-title class="red--text">
                <v-icon color="red" class="mr-2">mdi-cancel</v-icon>
                Remove penalty — Month {{ singleCancelDialog.payment ? singleCancelDialog.payment.month_number : '' }}
              </v-card-title>
              <v-card-text>
                <div class="mb-3">
                  This will <strong>fully remove</strong> the penalty of
                  <strong class="red--text">
                    {{ singleCancelDialog.payment ? formatAmount(singleCancelDialog.payment.penalty_applied) : '' }} FCFA
                  </strong>
                  for month {{ singleCancelDialog.payment ? singleCancelDialog.payment.month_number : '' }}.
                </div>
                <v-text-field
                  v-model="singleCancelDialog.password"
                  :type="singleCancelDialog.showPwd ? 'text' : 'password'"
                  :append-icon="singleCancelDialog.showPwd ? mdiEyeOffOutline : mdiEyeOutline"
                  outlined dense
                  label="Manager password"
                  @click:append="singleCancelDialog.showPwd = !singleCancelDialog.showPwd"
                  @keyup.enter="submitSingleCancel"
                />
              </v-card-text>
              <v-card-actions class="justify-end">
                <v-btn text @click="singleCancelDialog.open = false">Close</v-btn>
                <v-btn
                  color="red darken-1"
                  dark
                  :loading="singleCancellingId !== null"
                  :disabled="!singleCancelDialog.password"
                  @click="submitSingleCancel"
                >
                  Confirm removal
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Mass penalty cancellation dialog -->
          <v-dialog v-model="massCancelDialog.open" max-width="500">
            <v-card>
              <v-card-title class="red--text">
                <v-icon color="red" class="mr-2">mdi-cancel</v-icon>
                Remove penalties ({{ selectedPenalties.length }} selected)
              </v-card-title>
              <v-card-text>
                <div class="mb-3">This will <strong>fully remove</strong> the penalty for each selected month.</div>
                <v-text-field
                  v-model="massCancelDialog.password"
                  :type="massCancelDialog.showPwd ? 'text' : 'password'"
                  :append-icon="massCancelDialog.showPwd ? mdiEyeOffOutline : mdiEyeOutline"
                  outlined dense
                  label="Manager password"
                  @click:append="massCancelDialog.showPwd = !massCancelDialog.showPwd"
                />
              </v-card-text>
              <v-card-actions class="justify-end">
                <v-btn text @click="massCancelDialog.open = false">Close</v-btn>
                <v-btn color="red" dark :loading="massCancelling" :disabled="!massCancelDialog.password" @click="submitMassCancel">Confirm removal</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Payment form fields -->
          <v-col cols="12" sm="6" md="4" class="py-0 my-1">
            <v-text-field v-model="deposit_date" outlined dense type="date" label="Deposit date" @change="updatePreview" />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="py-0 my-1">
            <v-text-field v-model="amount" outlined dense label="Amount (FCFA)" @input="updatePreview" />
          </v-col>

          <!-- Simulation -->
          <v-col cols="12" class="py-0 my-1" v-if="paymentPreview">
            <v-alert :type="paymentPreview.isLate ? 'error' : 'info'" dense text prominent border="left" elevation="2">
              <div class="d-flex align-start flex-wrap">
                <v-icon large :color="paymentPreview.isLate ? 'red' : 'blue'" class="mr-3 mt-1">
                  {{ paymentPreview.isLate ? 'mdi-alert-circle' : 'mdi-calculator' }}
                </v-icon>
                <div>
                  <div class="font-weight-bold">
                    Payment month: <span class="primary--text">{{ getMonthName(paymentPreview.paymentMonthIndex) }}</span>
                    ({{ paymentPreview.paymentMonthIndex }}/10)
                  </div>
                  <div class="caption">
                    Interest rate for this period: <strong>{{ (paymentPreview.interestRate * 100).toFixed(1) }}%</strong>
                  </div>
                  <div v-if="amountNumber > 0">
                    <span>Estimated interest earned: </span>
                    <strong class="green--text">{{ formatAmount(paymentPreview.estimatedInterest) }} FCFA</strong>
                    <span class="caption grey--text"> (on the monthly installment part only)</span>
                  </div>
                  <div class="caption grey--text mt-1">
                    ℹ️ Interest is calculated only on the amount that goes toward the monthly installment (penalties excluded).
                    The rate depends on the calendar month of the deposit.
                  </div>
                </div>
              </div>
            </v-alert>
          </v-col>

          <v-col cols="12" sm="6" md="4" class="py-0 my-1">
            <v-text-field
              v-model="currentPassword"
              :type="isCurrentPasswordVisible ? 'text' : 'password'"
              :append-icon="isCurrentPasswordVisible ? mdiEyeOffOutline : mdiEyeOutline"
              label="Manager password"
              outlined dense
              @click:append="isCurrentPasswordVisible = !isCurrentPasswordVisible"
            />
          </v-col>
        </v-row>

        <v-card-actions class="justify-end">
          <spinner v-if="submitting" />
          <v-btn v-else color="primary" :disabled="!canSubmit" @click="submitInvestmentPayment()">Pay Investment</v-btn>
        </v-card-actions>
      </v-card-text>
    </v-form>

    <!-- Success dialog -->
    <v-dialog v-model="successDialog.open" max-width="800" persistent>
      <v-card class="success-dialog-card">
        <v-card-title class="teal darken-4 white--text headline py-4">
          <v-icon large left color="white">fas fa-check-circle</v-icon>
          Payment successfully recorded
        </v-card-title>
        <v-card-text class="pt-4 pb-2">
          <div class="success-message-body">
            <div v-for="(line, idx) in parsedSuccessLines" :key="idx" :class="lineClass(line)">
              <span v-if="line.type === 'title'">
                <v-icon small color="teal darken-2" class="mr-2">fas fa-check</v-icon>
                {{ line.text }}
              </span>
              <span v-else-if="line.type === 'month'">
                <v-icon small color="blue darken-2" class="mr-2">fas fa-calendar-alt</v-icon>
                {{ line.text }}
              </span>
              <span v-else-if="line.type === 'warning'">
                <v-icon small color="orange" class="mr-2">fas fa-exclamation-triangle</v-icon>
                {{ line.text }}
              </span>
              <span v-else>{{ line.text }}</span>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="flex-wrap justify-end pb-4 pr-4">
          <v-btn color="teal darken-2" dark large class="mb-2 mb-sm-0" @click="redirectToInvestmentsList">
            <v-icon left>fas fa-list</v-icon>
            Back to Investments
          </v-btn>
          <v-btn text large @click="successDialog.open = false">
            <v-icon left>fas fa-plus-circle</v-icon>
            New Payment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mdiEyeOffOutline, mdiEyeOutline } from '@mdi/js'
import axios from 'axios'
import Spinner from '@/components/Spinner.vue'

export default {
  components: { Spinner },
  data() {
    return {
      loadingUsers: false,
      loadingInvestor: false,
      loadingInvestment: false,
      submitting: false,
      loadingRef: false,
      userQuery: '',
      users: [],
      selectedUserId: '',
      phoneNumber: '',
      investments: [],
      selectedInvestmentId: null,
      investmentDetails: null,
      selectedPenalties: [],
      massCancelling: false,
      massCancelDialog: { open: false, password: '', showPwd: false },
      // ← nouveau : cancel individuel
      singleCancelDialog: { open: false, payment: null, password: '', showPwd: false },
      singleCancellingId: null,
      deposit_date: new Date().toISOString().slice(0, 10),
      amount: '',
      payment_ref: '',
      currentPassword: '',
      isCurrentPasswordVisible: false,
      mdiEyeOffOutline,
      mdiEyeOutline,
      successDialog: { open: false, rawMessage: '' },
      paymentPreview: null,
    }
  },
  computed: {
    amountNumber() {
      const n = Number(this.amount)
      return isFinite(n) ? n : 0
    },
    canSubmit() {
      return (
        this.payment_ref &&
        this.selectedUserId &&
        this.selectedInvestmentId &&
        this.deposit_date &&
        this.currentPassword &&
        this.amountNumber > 0
      )
    },
    selectAll: {
      get() {
        if (!this.investmentDetails?.payments_with_penalty?.length) return false
        const total         = this.investmentDetails.payments_with_penalty.length
        const selectedCount = this.selectedPenalties.length
        return selectedCount === total ? true : selectedCount === 0 ? false : null
      },
      set(value) {
        this.selectedPenalties = value
          ? this.investmentDetails.payments_with_penalty.map(p => p.id)
          : []
      },
    },
    parsedSuccessLines() {
      if (!this.successDialog.rawMessage) return []
      return this.successDialog.rawMessage.split('\n').map(line => {
        line = line.trim()
        if (!line) return null
        if (line.startsWith('✅')) return { type: 'title', text: line }
        if (line.match(/^\s*-\s*Month/)) return { type: 'month', text: line.replace(/^\s*-\s*/, '') }
        if (line.startsWith('⚠️')) return { type: 'warning', text: line }
        return { type: 'normal', text: line }
      }).filter(Boolean)
    },
  },
  watch: {
    deposit_date() { this.updatePreview() },
    amount()       { this.updatePreview() },
    investmentDetails: { handler() { this.updatePreview() }, deep: true },
  },
  mounted() {
    this.getPaymentRef()
    document.title = 'Manager: Make Payment'
  },
  methods: {
    formatAmount(v) {
      return new Intl.NumberFormat('en-US').format(Number(v) || 0)
    },
    lineClass(line) {
      if (line.type === 'title')   return 'mb-3 font-weight-bold title-line'
      if (line.type === 'month')   return 'month-line-item'
      if (line.type === 'warning') return 'warning-line-item mt-2'
      return ''
    },
    getMonthName(index) {
      const months = ['January','February','March','April','May','June','July','August','September','October','November','December']
      if (!this.investmentDetails?.start_date) return `Month ${index}`
      const start = new Date(this.investmentDetails.start_date)
      const d = new Date(start)
      d.setMonth(start.getMonth() + index - 1)
      return months[d.getMonth()]
    },
    updatePreview() {
      if (!this.investmentDetails?.payments) { this.paymentPreview = null; return }
      const payments    = this.investmentDetails.payments
      const nextPayment = payments.find(p => p.status !== 'completed')
      if (!nextPayment) { this.paymentPreview = null; return }

      let paymentDate = this.deposit_date ? new Date(this.deposit_date) : new Date()
      if (isNaN(paymentDate.getTime())) paymentDate = new Date()

      const startDateStr = this.investmentDetails.start_date
      let startDate
      if (typeof startDateStr === 'string' && startDateStr.includes('-')) {
        const [y, m, d] = startDateStr.split('-').map(Number)
        startDate = new Date(y, m - 1, d)
      } else {
        startDate = new Date(startDateStr)
      }
      if (isNaN(startDate.getTime())) { this.paymentPreview = null; return }

      let monthDiff       = (paymentDate.getFullYear() - startDate.getFullYear()) * 12 + (paymentDate.getMonth() - startDate.getMonth())
      let paymentMonthIndex = Math.max(1, Math.min(10, monthDiff + 1))

      const rates = { 1:0.18, 2:0.165, 3:0.15, 4:0.135, 5:0.12, 6:0.105, 7:0.09, 8:0.075, 9:0.06, 10:0.045 }
      const interestRate = rates[paymentMonthIndex] || 0

      let estimatedInterest = 0
      if (this.amountNumber > 0 && interestRate > 0) {
        const principalAlreadyPaid = Math.min(nextPayment.amount_paid || 0, nextPayment.amount_due)
        const principalRemaining   = Math.max(0, nextPayment.amount_due - principalAlreadyPaid)
        const amountForPrincipal   = Math.min(this.amountNumber, principalRemaining)
        estimatedInterest          = amountForPrincipal * interestRate
      }

      this.paymentPreview = { paymentMonthIndex, interestRate, estimatedInterest, isLate: nextPayment.is_late }
    },

    // ── Cancel individuel ──────────────────────────────────────────────────────
    openSingleCancelDialog(payment) {
      this.singleCancelDialog = { open: true, payment, password: '', showPwd: false }
    },
    async submitSingleCancel() {
      const { payment, password } = this.singleCancelDialog
      if (!password || !payment) return
      this.singleCancellingId    = payment.id
      this.singleCancelDialog.open = false
      const fd = new FormData()
      fd.append('payment_id', payment.id)
      fd.append('password', password)
      try {
        const res = await axios.post('/api/v1/manager/investments/cancel-penalty/', fd)
        const msg = Array.isArray(res.data) ? res.data[0] : null
        if (msg?.category === 'success') {
          this.$store.commit('setSnackBarMessage', `Penalty for month ${payment.month_number} removed.`)
          this.$store.commit('setSnackBarColor', 'teal darken-4')
          this.$store.commit('activateSnackBar', true)
          await this.loadInvestmentDetails()
        } else {
          this.showError(msg?.text || 'Error removing penalty')
        }
      } catch (e) {
        this.showError(e.response?.data?.error || 'Network error')
      } finally {
        this.singleCancellingId = null
        this.singleCancelDialog = { open: false, payment: null, password: '', showPwd: false }
      }
    },

    // ── Cancel masse ───────────────────────────────────────────────────────────
    async getPaymentRef() {
      this.loadingRef = true
      try {
        const res      = await axios.get('/api/v1/manager/investments/create/payment_ref/')
        this.payment_ref = res.data
      } catch (e) { this.showError('Error generating reference') }
      finally { this.loadingRef = false }
    },
    async getUsers() {
      if (!this.userQuery) return
      this.loadingUsers = true
      try {
        const res  = await axios.get(`/api/v1/manager/get/clients/${this.userQuery}/`)
        this.users = res.data || []
      } catch (e) { this.showError(e.response?.statusText || 'Network error') }
      finally { this.loadingUsers = false }
    },
    async loadInvestorDetails() {
      if (!this.selectedUserId) return
      this.loadingInvestor = true
      try {
        const res  = await axios.get(`/api/v1/manager/investments/investor-details/${this.selectedUserId}/`)
        const data = res.data || {}
        this.phoneNumber  = data.user?.phone_number || ''
        this.investments  = (data.investments || []).map(inv => ({
          id   : inv.id,
          label: `#${inv.id} · ${this.formatAmount(inv.investment_amount)} FCFA · ${inv.payment_type} · ${inv.status}`,
        }))
      } catch (e) { this.showError('Error loading investor') }
      finally { this.loadingInvestor = false }
    },
    async loadInvestmentDetails() {
      if (!this.selectedInvestmentId) return
      this.loadingInvestment  = true
      this.selectedPenalties  = []
      try {
        const res = await axios.get(`/api/v1/manager/investments/investment-details/${this.selectedInvestmentId}/`)
        this.investmentDetails = res.data || null
        this.updatePreview()
      } catch (e) { this.showError('Error loading investment') }
      finally { this.loadingInvestment = false }
    },
    async submitInvestmentPayment() {
      if (!this.canSubmit) return
      this.submitting = true
      const fd = new FormData()
      fd.append('investment_id', this.selectedInvestmentId)
      fd.append('payment_ref',   this.payment_ref)
      fd.append('amount',        this.amountNumber)
      fd.append('password',      this.currentPassword)
      fd.append('deposit_date',  this.deposit_date)
      try {
        const res = await axios.post('/api/v1/manager/investments/deposit/', fd)
        const msg = Array.isArray(res.data) ? res.data[0] : null
        if (msg?.category === 'success') {
          this.successDialog.rawMessage = msg.text
          this.successDialog.open       = true
          await this.loadInvestmentDetails()
          this.amount = ''
          await this.getPaymentRef()
        } else {
          this.showError(msg?.text || 'An error occurred')
        }
      } catch (err) {
        this.showError(err.response?.data?.[0]?.text || err.response?.data?.error || err.message)
      } finally {
        this.submitting = false
      }
    },
    redirectToInvestmentsList() {
      this.successDialog.open = false
      this.$router.push({ name: 'manager.investments.list' })
    },
    openMassCancelDialog() {
      if (this.selectedPenalties.length === 0) return
      this.massCancelDialog.password = ''
      this.massCancelDialog.open     = true
    },
    async submitMassCancel() {
      const password = this.massCancelDialog.password
      if (!password) return
      this.massCancelling = true
      const failed = [], succeeded = []
      for (const paymentId of this.selectedPenalties) {
        const fd = new FormData()
        fd.append('payment_id', paymentId)
        fd.append('password',   password)
        try {
          const res = await axios.post('/api/v1/manager/investments/cancel-penalty/', fd)
          const msg = Array.isArray(res.data) ? res.data[0] : null
          if (msg?.category === 'success') succeeded.push(paymentId)
          else failed.push(paymentId)
        } catch (e) { failed.push(paymentId) }
      }
      this.massCancelDialog.open = false
      this.massCancelling        = false
      if (succeeded.length) {
        this.$store.commit('setSnackBarMessage', `${succeeded.length} penalty(ies) removed successfully.`)
        this.$store.commit('setSnackBarColor', 'teal darken-4')
        this.$store.commit('activateSnackBar', true)
      }
      if (failed.length) this.showError(`${failed.length} penalty(ies) failed: ${failed.join(', ')}`)
      await this.loadInvestmentDetails()
      this.selectedPenalties = []
    },
    showError(m) {
      this.$store.commit('setSnackBarMessage', m)
      this.$store.commit('setSnackBarColor', 'red darken-4')
      this.$store.commit('activateSnackBar', true)
    },
  },
}
</script>

<style scoped>
.success-dialog-card { border-radius: 20px; overflow: hidden; }
.success-message-body {
  max-height: 500px; overflow-y: auto; padding: 8px;
  font-size: 0.95rem; background: #f5f5f5; border-radius: 12px;
}
.title-line {
  display: flex; align-items: center; font-size: 1.1rem; font-weight: bold;
  color: #004d40; background: #e0f2f1; padding: 10px; border-radius: 10px; margin-bottom: 12px;
}
.month-line-item {
  display: flex; align-items: flex-start; margin: 8px 0; padding: 6px 12px;
  background: white; border-left: 4px solid #1976d2; border-radius: 8px;
  font-family: monospace; font-size: 0.85rem;
}
.warning-line-item {
  display: flex; align-items: center; background: #fff3e0;
  padding: 8px 12px; border-radius: 8px; color: #e65100; font-weight: bold;
}
@media (max-width: 600px) {
  .success-message-body { font-size: 0.8rem; }
  .month-line-item { font-size: 0.75rem; }
}
</style>
